import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { useState, useEffect, useRef } from 'react';
import Container from '@mui/material/Container';
import { useLocation } from 'react-router-dom';
import { GetSessionContext } from '../../data/apiCalls';
import { idleTracker } from '../../scripts/idleTracker';
import useSessionContext from '../../data/queryHooks/useSessionContext';
import useHasDefaultLocation from '../../hooks/useManageDefaultLocation';
import { PAGE_ROUTES, TOP_ROUTES } from '../../scripts/routes';
import { LOGOUT_URL } from '../../scripts/navigationItems';
import { AnalyticsEvents, trackAnalyticsEvent } from '../../utils/analytics.js';
import CustomLoading from '../../components/shared/CustomLoading';
import ProtectedRoute from './ProtectedRoute/ProtectedRoute';
import ScrollToTop from './ScrollToTop';
import Header from './Header/Header';
import Footer from './Footer/Footer';
import IdleModal from './Modal/IdleModal';

const RootLayout = styled('div')`
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	grid-template-rows: auto 1fr auto;
	grid-template-areas:
		'header'
		'main'
		'footer';
	min-height: 100vh;

	.header {
		grid-area: header;
	}

	.main {
		grid-area: main;
	}

	.footer {
		grid-area: footer;
	}
`;

const VALUE_ADDED_PROGRAM_PATH = `/${TOP_ROUTES.resources}/${PAGE_ROUTES.valueAddedProgram}`;

const Layout = () => {
	const { isLoading, isLoggedIn } = useSessionContext();
	const { hasDefaultLocation } = useHasDefaultLocation();
	const location = useLocation();
	const previousPathnameRef = useRef(location.pathname);
	const [modalOpen, setModalOpen] = useState(false);
	const [staySingedIn, setStaySignedIn] = useState(false);

	const openModal = () => {
		setModalOpen(true);
	};

	const checkSession = async () => {
		try {
			const res = await GetSessionContext();
			if (res?.resultCode !== 0)
				window.open(LOGOUT_URL, '_self', 'noreferrer');
		} catch (error) {
			return error;
		}
		return null;
	};
	useEffect(() => {
		const previousPathname = previousPathnameRef.current;

		if (
			previousPathname === VALUE_ADDED_PROGRAM_PATH &&
			location.pathname !== VALUE_ADDED_PROGRAM_PATH
		) {
			trackAnalyticsEvent(
				AnalyticsEvents.VALUE_ADDED_PROGRAM_EXITED_INTERNAL,
				{
					page_key: 'value_added_program',
					page_path: VALUE_ADDED_PROGRAM_PATH,
					destination_path: location.pathname,
				}
			);
		}

		previousPathnameRef.current = location.pathname;
		checkSession();
	}, [location.pathname]);
	useEffect(() => {
		if (staySingedIn) {
			setModalOpen(false);
			idleTracker(isLoggedIn, openModal);
			setStaySignedIn(false);
		}
	}, [staySingedIn]);

	useEffect(() => {
		idleTracker(isLoggedIn, openModal);
	}, [isLoggedIn]);

	if (isLoading) {
		return (
			<Container sx={{ mt: 7 }}>
				<CustomLoading />
			</Container>
		);
	}

	if (!isLoggedIn) {
		return (
			<Container sx={{ mt: 7 }}>
				<CustomLoading />
			</Container>
		);
	}
	return (
		<RootLayout>
			<Header
				className='header'
				hasDefaultLocation={hasDefaultLocation}
			/>
			<IdleModal
				open={modalOpen}
				setSignedIn={() => setStaySignedIn(true)}
			/>
			<Box className='main' component='main' sx={{ mb: 5 }}>
				<ScrollToTop />
				<ProtectedRoute />
			</Box>
			<Footer
				className='footer'
				hasDefaultLocation={hasDefaultLocation}
			/>
		</RootLayout>
	);
};

export default Layout;
